import axios, { AxiosRequestConfig, InternalAxiosRequestConfig } from 'axios';
import React from 'react';
import { useCallback, useEffect, useState } from 'react';

import { useConfig } from './config';
import { useSSOStore } from './store/sso';

interface AxiosConfigProps {
  disableAuth?: boolean;
}

const AxiosConfig: React.FC<AxiosConfigProps> = ({ disableAuth, children }) => {
  const senseviewUri = useConfig((state) => state.api.senseview.api_uri);
  const { token, setToken } = useSSOStore(
    useCallback(({ token, setToken }) => ({ token, setToken }), []),
  );

  const [intercepted, setIntercepted] = useState(false);

  const interceptor = useCallback(
    (request: InternalAxiosRequestConfig): InternalAxiosRequestConfig => {
      if (token) {
        request.headers.set('Authorization', `Bearer ${token?.jwt}`);
      }

      return request;
    },
    [token],
  );

  useEffect(() => {
    axios.defaults.baseURL = senseviewUri;

    if (disableAuth) {
      setIntercepted(true);
      return () => {
        setIntercepted(false);
      };
    }

    // console.log('adding axios interceptors');
    const requestInterceptor = axios.interceptors.request.use(interceptor);

    const responseInterceptor = axios.interceptors.response.use(
      undefined,
      (error) => {
        console.log('interceptor error', error);
        if (!error.response) {
          throw new Error('CORS error');
        }
        console.log('interceptor error', error);
        if (error.response?.status === 401) {
          setToken();
        }
      }, // 401 error => logout
    );
    setIntercepted(true);

    return () => {
      // console.log('clearing axios interceptors');
      axios.interceptors.request.eject(requestInterceptor);
      axios.interceptors.response.eject(responseInterceptor);
      setIntercepted(false);
    };
  }, [senseviewUri, disableAuth, interceptor, setToken]);

  if (!intercepted) {
    return null;
  }

  return <>{children}</>;
};

export default AxiosConfig;
